import React, { useEffect, useState } from "react";
import "./ProspectingForm.scss";
import CustomOutlinedInput from "components/CustomOutlinedInput/CustomOutlinedInput";
import { Checkbox, FormControlLabel } from "@mui/material";
import AudienceMultiSelect from "./AudienceMultiSelect";
import DataSourceItem from "./components/DataSourceItem";
import { AddCircleOutlineRounded } from "@mui/icons-material";
import { validateAge, validateGender, validateBookingWindow } from "features/meta-travel-ads-campaign/Utils/validators/audienceValidators";
import { useSnackbar } from "notistack";

const genders = ["All", "Male", "Female"];

const ProspectingForm = ({
  ageRange,
  setAgeRange,
  selectedGender,
  setSelectedGender,
  isBookingWindowEnabled,
  setIsBookingWindowEnabled,
  bookingWindowRange,
  setBookingWindowRange,
  rules = [],
  setRules,
  errors,
  selectedIncludeAudiences = [],
  setSelectedIncludeAudiences,
  metaAudienceOptions = [],
  isLoadingMetaAudienceOptions = false,
  isSharedAudienceEnabled = false,
}) => {
  const [ageStatus, setAgeStatus] = useState({min_age: null, max_age: null});
  const [bookingWindowErrors, setBookingWindowErrors] = useState({ start: false, end: false });
  const {enqueueSnackbar} = useSnackbar();

  useEffect(() => {
      if(Object.keys(errors).length !== 0) {
        Object.keys(errors).forEach((errorKey) => {
          enqueueSnackbar(errors[errorKey], {variant: 'error'});
        });
      }
    }, [errors]);

  const handleUpdateDataSource = (type, id, updatedItem) => {
      setRules(
        rules.map((item) => item.id === id && item.type === type ? {...updatedItem, id, type} : item)
      );
  };

  const handleGenderBlur = async () => {
    const result = await validateGender(selectedGender);
    if (result.success) {
        delete errors.gender;
    }
  };

  const handleAgeBlur = async () => {
    const result = await validateAge(Number(ageRange.min), Number(ageRange.max));
    if (result.success) {
        delete errors.age_min;
        delete errors.age_max;
    }

    setAgeStatus(result.success ? {min_age: result?.data?.age_min?.[0]?.errorMessage, max_age: result?.data?.age_max?.[0]?.errorMessage} : {min_age: result.error_min || "Invalid age", max_age: result.error_max || "Invalid age"});
    if(result?.data?.age_min?.[0]?.errorMessage) {
        enqueueSnackbar(result?.data?.age_min?.[0]?.errorMessage, {variant: 'error'});
    }

    if(result?.data?.age_max?.[0]?.errorMessage) {
        enqueueSnackbar(result?.data?.age_max?.[0]?.errorMessage, {variant: 'error'});
    }
  };

  const validateBookingWindowFields = (start, end) => {
    const startVal = start !== "" ? Number(start) : null;
    const endVal = end !== "" ? Number(end) : null;
    const isAllBlank = (startVal === null || start === "") && (endVal === null || end === "");
    if (isAllBlank) {
      setBookingWindowErrors({ start: false, end: false });
      return true;
    }
    const startError = !start || startVal <= 0;
    const endError = !end || endVal <= 0 || (startVal && endVal && endVal < startVal);
    setBookingWindowErrors({ start: startError, end: endError });
    return !startError && !endError;
  };

  const handleBookingWindowBlur = async () => {
    const isValid = validateBookingWindowFields(
      bookingWindowRange.booking_window_start,
      bookingWindowRange.booking_window_end
    );
    if (isValid && bookingWindowRange.booking_window_start && bookingWindowRange.booking_window_end) {
      const result = await validateBookingWindow(Number(bookingWindowRange.booking_window_start), Number(bookingWindowRange.booking_window_end));
      if (result.success) {
        delete errors.booking_window_start;
        delete errors.booking_window_end;
      }
    }
  };

  const handleAddDataSource = (type) => {
    if (type === "INCLUDE") {
      setRules([
        ...rules,
        { id: rules.length + 1, type: "INCLUDE" },
      ]);
    } else {
      setRules([
        ...rules,
        { id: rules.length + 1, type: "EXCLUDE" },
      ]);
    }
  };

  const handleRemoveDataSource = (type, id) => {
      // Ne retirer que l'élément correspondant à l'id et au type, conserver tous les autres
      setRules(
        rules.filter((item) => !(item.id === id && item.type === type))
      );
  };

  return (
    <div className="prospecting-form">
      <p className="section-title">AUDIENCE DEMOGRAPHIC :</p>
      <div className="demographic-filters">
        <div className="filter-item">
          <span className="filter-label">AGE</span>
          <div className="age-inputs">
            <CustomOutlinedInput
              inputPaddingLeft={"5px"}
              minWidth={"42px"}
              inputPadding={"0px"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{ maxWidth: "42px" }}
              value={ageRange.min}
              onChange={(e) => setAgeRange({ ...ageRange, min: e.target.value })}
              error={!!errors?.age_min || Boolean(ageStatus.min_age)}
              onBlur={handleAgeBlur}
            />
            <span> - </span>
            <CustomOutlinedInput
              inputPaddingLeft={"5px"}
              minWidth={"42px"}
              value={ageRange.max}
              onChange={(e) => setAgeRange({ ...ageRange, max: e.target.value })}
              onBlur={handleAgeBlur}
              inputPadding={"0px"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{ maxWidth: "42px" }}
              error={!!errors?.age_max || Boolean(ageStatus.max_age)}
            />
            <span className="filter-label">Years Old</span>
          </div>
        </div>
        <div className="filter-item">
          <span className="filter-label">GENDER</span>
          <div>
            {genders.map((gender) => (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    sx={{
                      paddingBlock: "2px",
                      color: "#BAB7B7",
                      "&.Mui-checked": {
                        color: "#61D5A9",
                      },
                      "& .MuiSvgIcon-root": {
                        height: "16px",
                        width: "16px",
                      },
                    }}
                    value={gender.toUpperCase()}
                    checked={selectedGender === gender.toUpperCase()}
                    onChange={() => setSelectedGender(gender.toUpperCase())}
                  />
                }
                onBlur={handleGenderBlur}
                sx={{
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "12px",
                    fontFamily: "Inter",
                    fontWeight: 400,
                    color: "#373F41",
                  },
                }}
                label={gender}
              />
            ))}
          </div>
        </div>
      </div>
      <p className="section-title">DATA SOURCES :</p>
      <div className="data-sources">
      <div className="inclusions-exclusions" style={{ flexDirection: "column" }}>
        {/* Include Audience from library */}
        {setSelectedIncludeAudiences && isSharedAudienceEnabled && (
          <div className="section" style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span className="filter-label" style={{ whiteSpace: "nowrap" }}>INCLUDE AUDIENCE:</span>
              <AudienceMultiSelect
                value={selectedIncludeAudiences}
                onChange={setSelectedIncludeAudiences}
                options={metaAudienceOptions}
                placeholder="Select audiences to include"
                width={421}
                checkedColor="#61D5A9"
              />
            </div>
          </div>
        )}
        <div className="section">
          <span className="filter-label">EXCLUDE:</span>
          {rules.filter(rule => rule.type === "EXCLUDE").map((item) => (
            <DataSourceItem key={item.id} id={item.id} item={item} onRemove={() => handleRemoveDataSource("EXCLUDE", item.id)} onChange={(id,updatedItem) => handleUpdateDataSource("EXCLUDE", id, updatedItem)}/>
          ))}
          <span className="add-data-source" onClick={() => handleAddDataSource("EXCLUDE")}><AddCircleOutlineRounded sx={{fontSize: 18}}/> ADD EXCLUSION</span>
        </div>
      </div>
      <p className="section-title">OPTIONS:</p>
      <div className="data-sources">
        <div className="booking-window-section">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                sx={{
                  paddingBlock: "2px",
                  color: "#BAB7B7",
                  "&.Mui-checked": {
                    color: "#61D5A9",
                  },
                  "& .MuiSvgIcon-root": {
                    height: "16px",
                    width: "16px",
                  },
                }}
                checked={isBookingWindowEnabled}
                onChange={(e) => setIsBookingWindowEnabled(e.target.checked)}
              />
            }
            sx={{
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                fontSize: "12px",
                fontFamily: "Inter",
                fontWeight: 400,
                color: "#373F41",
              },
            }}
            label={"BOOKING WINDOW:"}
          />
          <span className="info-text">
            Limit audience to people within the following booking window:
          </span>
          {errors?.booking_window_start && <span className="error-text">{errors.booking_window_start}</span>}
          {errors?.booking_window_end && <span className="error-text">{errors.booking_window_end}</span>}
        </div>
        {isBookingWindowEnabled && (
          <div className="age-inputs">
            <CustomOutlinedInput
              inputPaddingLeft={"5px"}
              minWidth={"42px"}
              inputPadding={"0px"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{ maxWidth: "42px" }}
              value={bookingWindowRange.booking_window_start}
              onChange={(e) => {
                const val = e.target.value;
                setBookingWindowRange({
                  ...bookingWindowRange,
                  booking_window_start: val,
                });
                validateBookingWindowFields(val, bookingWindowRange.booking_window_end);
              }}
              onBlur={handleBookingWindowBlur}
              error={bookingWindowErrors.start || !!errors?.booking_window_start}
            />
            <span> - </span>
            <CustomOutlinedInput
              inputPaddingLeft={"5px"}
              minWidth={"42px"}
              inputPadding={"0px"}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              sx={{ maxWidth: "42px" }}
              value={bookingWindowRange.booking_window_end}
              onChange={(e) => {
                const val = e.target.value;
                setBookingWindowRange({
                  ...bookingWindowRange,
                  booking_window_end: val,
                });
                validateBookingWindowFields(bookingWindowRange.booking_window_start, val);
              }}
              onBlur={handleBookingWindowBlur}
              error={bookingWindowErrors.end || !!errors?.booking_window_end}
            />
            <span className="filter-label">
              Days until their date of travel
            </span>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default ProspectingForm;
